import { useCallback, useState, useEffect } from "react";
import usePolling from "@zenith/hooks/usePolling";

/**
 * Creates a standardized polling hook for any type of verification or status checking
 *
 * @param options Configuration options for the polling hook
 * @returns A standardized polling hook interface
 */
export function createPollingHook<ResponseType, StatusType>(options: {
  /**
   * A unique identifier for this type of polling (e.g., "nin-verification", "document-verification")
   */
  pollingType: string;

  /**
   * Function to fetch the status
   */
  fetchFunction: () => Promise<ResponseType>;

  /**
   * Extract the status from the response
   */
  getStatus: (response: ResponseType) => StatusType;

  /**
   * List of terminal statuses (statuses that indicate polling should stop)
   */
  terminalStatuses: StatusType[];

  /**
   * Default polling configuration
   */
  defaultPollingConfig?: {
    interval?: number;
    maxRetries?: number;
    maxPollingTime?: number;
  };

  /**
   * Optional callback to be executed when a status changes
   */
  onStatusChange?: (status: StatusType) => void;
}) {
  const DEBUG = process.env.NODE_ENV === "development";
  const log = (...args: any[]) =>
    DEBUG && console.log(`[${options.pollingType}Polling]`, ...args);

  return function useCustomPolling(
    // Whether polling should be active
    triggerPolling: boolean,
    // Whether the system is currently waiting/processing
    isProcessing: boolean,
    // Unique instance ID (optional)
    instanceId?: string,
    // Whether the hook is enabled
    enabled = true
  ) {
    const [status, setStatus] = useState<StatusType | undefined>(undefined);
    const [lastError, setLastError] = useState<Error | null>(null);
    const [lastPollingAttempt, setLastPollingAttempt] = useState<number | null>(
      null
    );

    // Create a unique polling ID for this verification session
    const pollingId = `${options.pollingType}-${instanceId || "default"}`;

    // Log polling state for debugging
    log("Initializing with params:", {
      triggerPolling,
      isProcessing,
      instanceId,
      enabled,
      pollingId,
    });

    // Use the base polling hook
    const { response, polling, error, reset } = usePolling<ResponseType>(
      triggerPolling && enabled,
      !isProcessing,
      options.fetchFunction,
      {
        pollingId,
        interval: options.defaultPollingConfig?.interval || 3000,
        maxPollingTime: options.defaultPollingConfig?.maxPollingTime || 300000,
        maxRetries: options.defaultPollingConfig?.maxRetries || 100,
        debug: DEBUG,
      }
    );

    // Track polling attempts and errors
    useEffect(() => {
      if (polling) {
        setLastPollingAttempt(Date.now());
        log("Polling started/continuing at:", new Date().toISOString());
      }
    }, [polling]);

    // Handle errors from base polling hook
    useEffect(() => {
      if (error) {
        log("Error received from base polling:", error);
        setLastError(error);
      }
    }, [error]);

    // Process verification response
    useEffect(() => {
      if (!response) return;

      log("Received response:", response);
      const currentStatus = options.getStatus(response);

      // Check if this is a terminal status
      const isTerminalStatus = options.terminalStatuses.includes(currentStatus);

      // Always update status
      setStatus(currentStatus);

      // Call the optional callback if provided
      if (options.onStatusChange) {
        options.onStatusChange(currentStatus);
      }

      // If we've reached a terminal status, stop polling
      if (isTerminalStatus) {
        log(`Polling completed with status: ${String(currentStatus)}`);
        reset();
      }
    }, [response, reset]);

    // Enhanced reset function
    const enhancedReset = useCallback(() => {
      log("Resetting polling state explicitly");
      setStatus(undefined);
      setLastError(null);
      setLastPollingAttempt(null);
      reset();

      // Clear any storage entries that might interfere with future polling
      try {
        localStorage.removeItem(`polling_session_${pollingId}`);
      } catch (e) {
        log("Failed to clear localStorage entry:", e);
      }
    }, [reset, pollingId]);

    // Clean up when component unmounts
    useEffect(() => {
      log(`Setting up ${options.pollingType} polling with ID ${pollingId}`);

      // Check for any stale polling entries in localStorage
      try {
        const storedValue = localStorage.getItem(
          `polling_session_${pollingId}`
        );
        if (storedValue) {
          const timestamp = parseInt(storedValue, 10);
          const age = Date.now() - timestamp;
          log(`Found existing polling entry, age: ${age}ms`);

          // If older than 10 minutes, consider it stale and remove
          if (age > 600000) {
            log("Removing stale polling entry");
            localStorage.removeItem(`polling_session_${pollingId}`);
          }
        }
      } catch (e) {
        log("Error checking localStorage:", e);
      }

      return () => {
        log(`Cleaning up ${options.pollingType} polling with ID ${pollingId}`);
        reset();
      };
    }, [reset, pollingId]);

    return {
      status,
      polling,
      error: lastError || error,
      reset: enhancedReset,
      lastPollingAttempt,
    };
  };
}

export default createPollingHook;
